import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export interface StepperStep {
  id: string;
  label: string;
  done: boolean;
  /** A dónde lleva el paso mientras está pendiente. */
  to?: string;
  /** Nombre accesible completo; el visible es `label`. */
  ariaLabel?: string;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Stepper de píldoras, horizontal.
 *
 * Anatomía por paso: indicador redondo (número → palomita al cumplirse)
 * + píldora con el texto + separador que une con el siguiente. El
 * separador se colorea SOLO cuando su paso está cumplido, así que la
 * línea de color mide de un vistazo cuánto se lleva andado.
 *
 * ── Por qué no es el stepper de shadcn ───────────────────────────────
 * El que circula trae `components/ui`, `@/lib/utils`, tokens
 * `bg-primary`/`text-muted-foreground` y `@radix-ui/react-icons`. Este
 * proyecto tiene su design system y sus tokens (`ds-*`), y `lucide` ya
 * da la palomita: copiarlo entero metería un segundo sistema de estilos
 * para pintar tres píldoras. Se conserva su ANATOMÍA —indicador, título,
 * separador, estados— y se descarta su plomería.
 *
 * ── Estados ──────────────────────────────────────────────────────────
 * `done` cumplido (verde, palomita, ya no es enlace: no queda nada que
 * ir a hacer) · el PRIMER pendiente es el activo (anillo y sombra, es el
 * que toca) · el resto, pendientes en morado claro.
 *
 * Las animaciones se apagan solas si el sistema pide menos movimiento.
 */
export function Stepper({ steps, className }: { steps: StepperStep[]; className?: string }) {
  const reduce = useReducedMotion();
  const activeIndex = steps.findIndex((step) => !step.done);

  return (
    /*
      `w-full` + separadores elásticos: el stepper ocupa el ancho entero
      (Anahí, 28-ago) en vez de amontonarse a la izquierda. En pantallas
      estrechas las píldoras se apilan y los separadores se esconden —una
      línea de 4 px entre dos píldoras apiladas no une nada.
    */
    <ol
      className={cn("flex w-full flex-wrap items-center gap-y-3", className)}
      data-orientation="horizontal"
    >
      {steps.map((step, index) => {
        const active = index === activeIndex;
        const pill = cn(
          "inline-flex items-center gap-2.5 rounded-ds-pill py-2 pl-2 pr-4 text-[13.5px] font-semibold transition-colors",
          step.done
            ? "bg-ds-success-soft text-ds-ink"
            : active
              ? "bg-ds-lavender-bg-2 text-ds-violet-pop ring-2 ring-ds-violet-pop/30"
              : "bg-ds-lavender-bg text-ds-violet-pop hover:bg-ds-lavender-bg-2",
        );

        const indicator = (
          <span
            className={cn(
              "relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold transition-colors",
              step.done ? "bg-ds-ink text-white" : "bg-white text-ds-violet-pop shadow-ds-xs",
            )}
          >
            <AnimatePresence initial={false} mode="wait">
              {step.done ? (
                <motion.span
                  key="check"
                  initial={reduce ? false : { scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={reduce ? undefined : { scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 24 }}
                  className="flex"
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                </motion.span>
              ) : (
                <motion.span
                  key="number"
                  initial={reduce ? false : { scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={reduce ? undefined : { scale: 0.6, opacity: 0 }}
                >
                  {index + 1}
                </motion.span>
              )}
            </AnimatePresence>
            {/* El halo del paso que toca, como el pulso de la referencia. */}
            {active && !reduce && (
              <motion.span
                aria-hidden="true"
                className="absolute inset-0 rounded-full ring-2 ring-ds-violet-pop"
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: [0.55, 0, 0.55], scale: [1, 1.45, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </span>
        );

        const content = (
          <>
            {indicator}
            <span>{step.label}</span>
          </>
        );

        return (
          <li
            key={step.id}
            className={cn("flex items-center", index < steps.length - 1 && "flex-1")}
          >
            {step.done || !step.to ? (
              <span className={pill} aria-label={step.ariaLabel ?? step.label}>
                {content}
              </span>
            ) : (
              <Link to={step.to} className={pill} aria-label={step.ariaLabel ?? step.label}>
                {content}
              </Link>
            )}

            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                /*
                  Grosor 4 px y lavanda medio: con 2 px sobre el crema
                  del panel la línea casi no se veía (Anahí, 28-ago). La
                  pista tiene que leerse SOLA para que el tramo relleno
                  signifique algo.
                */
                className="mx-2.5 hidden h-1 flex-1 overflow-hidden rounded-full bg-ds-lavender-bg-2 sm:block"
              >
                {/* Se llena al cumplirse el paso: la línea de color ES el avance. */}
                <motion.span
                  className="block h-full rounded-full bg-ds-ink"
                  initial={false}
                  animate={{ width: step.done ? "100%" : "0%" }}
                  transition={reduce ? { duration: 0 } : { duration: 0.5, ease: "easeOut" }}
                />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
